import { buttonClassName } from "@/components/ui/Button";

export interface ContactQuickLinksProps {
  mobilePhone: string;
  email: string | null;
  className?: string;
}

/**
 * The one-click contact links — real `tel:`/`sms:`/`mailto:` anchors built
 * from a person's own stored phone/email, never a click handler: this is
 * what lets "open the device's normal phone or mail app" work at all,
 * keyboard/right-click/"open in a new tab" included.
 *
 * Story 6.2 built this markup inline on `TenantRecord.tsx` (VC-6.2-09/
 * VC-6.2-10). Story 6.5 extracts it here — the lease record page's own
 * co-tenant list is the second real call site for the identical markup,
 * the point past which this codebase has consistently chosen to share
 * rather than duplicate (mirrors `tools/route_prefixes.py`'s own "second
 * consumer, extract now" precedent, Story 6.2's fix round). Both
 * `TenantRecord.tsx` and `LeaseRecord.tsx`'s own co-tenant section render
 * this exact component now — never a second, drifted copy of the markup.
 *
 * `buttonClassName("secondary", ...)` gives each link the real
 * `h-tap-target-min` (44px) button height; `min-w-tap-target-min`
 * guarantees the matching 44px minimum width even for the shortest label
 * ("Call"). `mobilePhone` is required on every caller of this component
 * today (`Tenant.mobile_phone`/`Contact.mobile_phone` are both `NOT NULL`),
 * so `tel:`/`sms:` always render; `email` is nullable, so the `mailto:`
 * link renders only when one is actually on file — never a broken link to
 * nothing.
 */
export function ContactQuickLinks({ mobilePhone, email, className }: ContactQuickLinksProps) {
  return (
    <div className={["flex flex-wrap gap-2", className].filter(Boolean).join(" ")}>
      <a href={`tel:${mobilePhone}`} className={buttonClassName("secondary", "min-w-tap-target-min")}>
        Call
      </a>
      <a href={`sms:${mobilePhone}`} className={buttonClassName("secondary", "min-w-tap-target-min")}>
        Text
      </a>
      {email ? (
        <a href={`mailto:${email}`} className={buttonClassName("secondary", "min-w-tap-target-min")}>
          Email
        </a>
      ) : null}
    </div>
  );
}
